import { getTabIndex } from "@components/auditTrail/Utils";
import BusyIndicator from "@components/busyIndicator";
import { BusyIndicatorSize } from "@components/busyIndicator/BusyIndicator.utils";
import { composeRefHandlers } from "@utils/general";
import { Status } from "enums";
import React from "react";
import TestIds from "testIds";

import { useInputEventHandlers } from "./hooks/useInputHandlers";
import { IInputPropsExtended } from "./Input";
import {
    FakeTabbingButton,
    IconDelimiter,
    IconWrapper,
    InputHoverContainer,
    InputStatus,
    LinkWrapper,
    SecondaryIconWrapper,
    StyledContent,
    StyledHtmlInput,
    StyledInput,
    StyledTextArea,
    StyledValueUnit
} from "./Input.styles";


interface IIconProps {
    onClick: (e: React.MouseEvent) => void;
    hasAction: boolean;
    isActive: boolean;
    isSharpRight: boolean;
    icon: React.ReactElement;
    renderWrapperOnly: boolean;
    isDisabled?: boolean;
}

const Icon = (props: IIconProps) => {
    return (
        <IconWrapper
            data-testid={TestIds.DropdownIcon}
            onClick={props.onClick}
            isActive={props.isActive}
            isSharpRight={props.isSharpRight}
            isDisabled={props.isDisabled}
        >
            {!props.renderWrapperOnly && (
                <>
                    {props.hasAction && <IconDelimiter />}
                    {props.icon}
                </>
            )}
        </IconWrapper>
    );
};


const SecondaryIcon = (props: IInputPropsExtended) => {
    if (props.secondaryIcon) {
        return (
            <SecondaryIconWrapper data-testid={TestIds.Icon} hasIcon={!!props.icon}>
                {props.secondaryIcon}
            </SecondaryIconWrapper>
        );
    }

    return null;
};

const IconRenderer = ({isOnTopLevel, handlers,  ...props}: WithInputEventHandlers<IInputPropsExtended> & { isOnTopLevel: boolean}) => {
    if (props.icon || props.isLoading) {
        return (
            <>
                {props.isLoading && <BusyIndicator size={BusyIndicatorSize.XS} isDelayed />}
                {props.iconHasDifferentAction && isOnTopLevel && (
                    <FakeTabbingButton
                        tabIndex={props.isDisabled ? -1 : 0}
                        onKeyDown={handlers.handleFakeInputKeyDown}
                    />
                )}
                <Icon
                    icon={props.icon}
                    onClick={handlers.handleOnIconClick}
                    hasAction={!!props.onIconClick}
                    isActive={!isOnTopLevel && props.isActive}
                    renderWrapperOnly={!isOnTopLevel}
                    isSharpRight={props.isSharpRight}
                    isDisabled={props.isDisabled}
                />
            </>
        );
    }

    return null;
};


type WithInputEventHandlers<T> = T & { handlers: ReturnType<typeof useInputEventHandlers> };

interface InputRendererProps {
    props: IInputPropsExtended
    handlers: ReturnType<typeof useInputEventHandlers>;
    inputRef: React.RefObject<HTMLInputElement>;
    wrapperRef: React.RefObject<HTMLElement>;
  }

export const InputRenderer =({ props, handlers, inputRef, wrapperRef }: InputRendererProps) => {
    const StyledInputComponent: any = props.isMultiLine ? StyledTextArea : StyledHtmlInput;
    const showError = props.hasError;
    const showErrorText = props.hasErrorText;

    const getMaxRows = (): number => {
        return props.maxRows || 1;
    };

    const styleProps = {
        isSharpLeft: props.isSharpLeft,
        isSharpRight: props.isSharpRight,
        isSynchronized: props.isSynchronized,
        isDisabled: props.isDisabled && !props.auditTrailData?.type,
    };

    // turn of browser complete for every input
    // https://developer.mozilla.org/en-US/docs/Web/Security/Practical_implementation_guides/Turning_off_form_autocompletion#managing_autofill_for_login_fields
    const autocomplete = props.autocomplete ?? "new-password";

    const { unit } = props;

    const { width, ...inputProps } = { ...props.inputProps };

    return <>
            {props.link && (
                <LinkWrapper hasIcon={!!props.icon} auditTrailType={props.auditTrailData?.type}>
                    {props.link}
                </LinkWrapper>
            )}
            <StyledInput
                className={props.className}
                auditTrailType={props.auditTrailData?.type}
                data-testid={TestIds.Input}
                onMouseDown={handlers.handleMouseDown}
                onClick={handlers.handleInputClick}
                ref={composeRefHandlers(wrapperRef, props.outerRef)}
                isMultiLine={props.isMultiLine}
                _width={props.width}
                _cursor={props.cursor}
                _height={props.height}
                _hasIcon={!!props.icon}
                {...styleProps}
            >
                <InputHoverContainer
                    _hasIcon={!!props.icon}
                    _hasUnit={!!unit}
                    textAlign={props.textAlign}
                    isActive={props.isActive}
                    isSharpLeft={props.isSharpLeft}
                    isSharpRight={props.isSharpRight}
                    isSynchronized={!props.isDisabled && props.isSynchronized}
                    isDisabled={styleProps.isDisabled}
                >
                    {props.contentBefore}
                    <StyledInputComponent
                        auditTrailType={props.auditTrailData?.type}
                        tabIndex={styleProps.isDisabled ? -1 : getTabIndex(props)}
                        ref={composeRefHandlers(inputRef, props.passRef)}
                        _cursor={props.cursor}
                        hasIcon={!!props.icon}
                        onFocus={handlers.handleFocus}
                        onBlur={handlers.handleBlur}
                        onWheel={props.onWheel}
                        showError={showError}
                        showErrorText={showErrorText}
                        hasSecondaryIcon={!!props.secondaryIcon}
                        value={props.link ? "" : props.value}
                        onChange={handlers.handleOnChange}
                        type={
                            props.type || "search" /* type search is needed so autocomplete="off" works in chrome */
                        }
                        // disable autofill for Dashlane password manage
                        // it would be nice to find something universal, but such attribute doesn't seem to exist
                        data-form-type={"other"}
                        onKeyDown={handlers.handleKeyDown}
                        onKeyPress={handlers.handleKeyPress}
                        id={props.id}
                        data-name={props.name}
                        textAlign={props.textAlign}
                        placeholder={props.isDisabled ? "" : props.placeholder}
                        autoComplete={autocomplete}
                        {...inputProps} // any other prop passed down to input element
                        _width={props.inputProps?.width}
                        maxRows={getMaxRows()}
                        disabled={styleProps.isDisabled}
                        {...styleProps}
                    />
                    <IconRenderer {...props}  isOnTopLevel={false} handlers={handlers} />
                    {unit && (
                        <StyledValueUnit hasErrorText={props.hasErrorText} data-testid={TestIds.Description}>
                            &nbsp;{unit}
                        </StyledValueUnit>
                    )}
                    {props.content && <StyledContent $hasIcon={!!props.icon}>{props.content}</StyledContent>}
                    {/* Error and tooltip from HOC */}
                    {props.errorAndTooltip}

                    <SecondaryIcon {...props} />
                </InputHoverContainer>
                <IconRenderer isOnTopLevel={true} handlers={handlers} {...props} />
            </StyledInput>
            {!props.isReadOnly && props.showChange && (
                <InputStatus
                    isSharpLeft={props.isSharpLeft}
                    status={Status.Warning}
                    data-testid={TestIds.InputStatus}
                />
            )}
        </>;

  };